import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";

import "mapbox-gl/dist/mapbox-gl.css";

import { useTheme } from "@material-ui/core/styles";
import { MAPBOX_TOKEN } from "lib/config";
import { StaticMap } from "react-map-gl";
import { WebMercatorViewport } from "@deck.gl/core";

import { useMediaQuery } from "@material-ui/core";

import DeckGL, { FlyToInterpolator } from "deck.gl";

import { debounce } from "lodash";

const flyInterpolator = new FlyToInterpolator({
  speed: 2,
  curve: 2,
  screenSpeed: 1,
});

// const linearInterpolator = new LinearInterpolator();

const INITIAL_VIEW_STATE = {
  bearing: 0,
  pitch: 0,
  longitude: 0,
  latitude: 0,
  zoom: 6,
};

function useDebouncedCallback(callback, delay, deps) {
  const ref = React.useRef(false);

  useEffect(() => {
    ref.current = true;
    return () => {
      ref.current = false;
    };
  }, []);

  const callbackfunc = useCallback(
    debounce((...args) => ref.current && callback(...args), delay),
    [deps]
  );

  return callbackfunc;
}

function getBoundsFromViewState(viewState) {
  const viewport = new WebMercatorViewport(viewState);

  const nw = viewport.unproject([0, 0]);
  const se = viewport.unproject([viewport.width, viewport.height]);

  const bounds = {
    south: se[1],
    north: nw[1],
    east: se[0],
    west: nw[0],
  };
  return bounds;
}

function BaseMap({
  layers,
  initialLatitude,
  initialLongitude,
  initialZoom,
  initialBbox,
  onBoundsChange,
}) {
  const [viewState, setViewState] = useState({
    ...INITIAL_VIEW_STATE,
    latitude: initialLatitude,
    longitude: initialLongitude,
    zoom: initialZoom,
  });

  useEffect(() => {
    if (
      initialZoom !== viewState.initialZoom &&
      initialLatitude !== viewState.initialLatitude &&
      initialLongitude !== viewState.initialLongitude
    ) {

      setViewState({
        ...viewState,
        zoom: initialZoom,
        latitude: initialLatitude,
        longitude: initialLongitude,
      });
      return;

    }

    if (initialBbox == null) {
      return;
    }

    const viewport = new WebMercatorViewport(viewState);
    const [minX, minY, maxX, maxY] = initialBbox;
    const newViewState = viewport.fitBounds([
      [minX, minY],
      [maxX, maxY],
    ]);

    setViewState(newViewState);
    // NOTE: I do not want to update the viewstate... if the viewstate changed. I only want to update it
    // if initialBbox changed. That is why this lint rule must be disabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBbox, initialZoom, initialLatitude, initialLatitude]);

  const handleViewStateChange = useCallback(
    ({ viewState: newViewState }) => {
      if (viewState !== newViewState) {
        setViewState(newViewState);
      }
    },
    [viewState]
  );

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("xs"), {
    defaultMatches: true,
  });

  const initialViewState = useMemo(() => {
    return {
      ...INITIAL_VIEW_STATE,
      transitionDuration: !isMobile ? 2500 : undefined,
      transitionInterpolator: !isMobile ? flyInterpolator : undefined,
    };
  }, [isMobile]);

  const initialized = useRef(false);

  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      console.log("Mounting BaseMap");

      return () => {
        console.log("Unmounting BaseMap");
      };
    }, []);

    console.log("Drawing base map");
  }

  const handleResize = useDebouncedCallback(
    ({ width, height }) => {
      if (!initialized.current) {
        initialized.current = true;
        const bounds = getBoundsFromViewState({
          ...initialViewState,
          width,
          height,
        });
        onBoundsChange(bounds);
      }
    },
    500,
    [onBoundsChange]
  );

  const handleViewState = useDebouncedCallback(
    (args) => {
      if (onBoundsChange != null) {
        const { viewState } = args;
        const bounds = getBoundsFromViewState(viewState);
        onBoundsChange(bounds);
        viewState.current = viewState;
      }
    },
    500,
    [onBoundsChange]
  );

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        margin: "auto",
        minHeight: 350,
        height: "100%",
      }}
    >
      <DeckGL
        height="100%"
        style={{ position: "absolute" }}
        onResize={handleResize}
        onViewStateChange={handleViewStateChange}
        controller={true}
        viewState={viewState}
        layers={layers}
      >
        <StaticMap
          mapboxApiAccessToken={MAPBOX_TOKEN}
          mapStyle="mapbox://styles/mapbox/light-v10"
        />
      </DeckGL>
    </div>
  );
}

export default React.memo(BaseMap);

if (process.env.NODE_ENV === "development") {
  BaseMap.whyDidYouRender = {
    logOnDifferentValues: false,
  };
}
